/**
 * Wrapper main-thread do worker de conversão de vídeo (`ffmpegVideo.worker.ts`).
 *
 * Mesma forma do `ffmpegM4a.ts` do áudio — worker único, vivo entre usos, com
 * progresso e cancelamento —, mas apontando para o core de VÍDEO em
 * `/ffmpeg-video/`. São dois cores distintos de propósito: o de áudio tem 1,2 MB
 * e não sabe nada de vídeo; o de vídeo é bem maior e só é baixado quando algum
 * vídeo realmente precisa de conversão.
 */

export interface ConversaoOptions {
    signal?: AbortSignal;
    onProgress?: (fraction: number) => void;
}

export interface PedidoDeConversao {
    blob: Blob;
    alvoBytes: number;
    bitrateVideo: number;
    bitrateAudio: number;
    larguraMaxima: number | null;
    duracaoSec: number;
    tentarRemux: boolean;
}

export interface ResultadoDaConversao {
    blob: Blob;
    /** `true` quando bastou trocar o container, sem reencodar nada. */
    remuxado: boolean;
}

type MensagemDoWorker =
    | { type: 'progress'; value: number }
    | { type: 'done'; buffer: ArrayBuffer; remuxado: boolean }
    | { type: 'cancelled'; message: string }
    | { type: 'warm' }
    | { type: 'error'; message: string };

const LOG = '[ffmpegVideo]';

const DEBUG = (() => {
    const env = import.meta.env as Record<string, string | undefined>;
    if (env.VITE_FFMPEG_DEBUG === 'true') return true;
    try {
        return typeof localStorage !== 'undefined'
            && localStorage.getItem('whiz.ffmpegDebug') === 'true';
    } catch {
        return false;
    }
})();
const dlog = (...args: unknown[]) => { if (DEBUG) console.log(...args); };

/** Base dos assets do core de vídeo. Lida AQUI (main thread), nunca dentro do
 *  worker: `import.meta.env` em worker sai do build com `__VITE_IS_LEGACY__`
 *  solto (mesma armadilha já documentada no caminho de áudio). */
const FFMPEG_VIDEO_BASE =
    (import.meta.env as Record<string, string | undefined>).VITE_FFMPEG_VIDEO_BASE
    || '/ffmpeg-video';

let sharedWorker: Worker | null = null;

function getWorker(): Worker {
    if (sharedWorker) return sharedWorker;
    dlog(`${LOG} subindo worker`);
    sharedWorker = new Worker(
        new URL('./ffmpegVideo.worker.ts', import.meta.url),
        { type: 'module' },
    );
    return sharedWorker;
}

/**
 * Começa a baixar o core AGORA. Vale chamar quando o usuário escolhe um vídeo
 * grande, antes de ele confirmar o envio: o core de vídeo é pesado e baixá-lo em
 * paralelo com a digitação da legenda esconde boa parte da espera.
 * Falha é silenciosa — a conversão real tenta de novo e é ela que reporta.
 */
export function warmUpVideoWorker(): void {
    try {
        getWorker().postMessage({
            type: 'warmup',
            base: FFMPEG_VIDEO_BASE,
            debug: DEBUG,
        });
    } catch (err) {
        dlog(`${LOG} warmup não pôde ser solicitado:`, err);
    }
}

/** Derruba o worker e o core carregado. */
export function disposeVideoWorker(): void {
    if (!sharedWorker) return;
    sharedWorker.terminate();
    sharedWorker = null;
}

/** Extensão de entrada para o ffmpeg a partir do mime do Blob. */
export function extensaoDeEntrada(type: string): string {
    const base = type.split(';').at(0)?.trim().toLowerCase() ?? '';
    if (base.includes('webm')) return 'webm';
    if (base.includes('matroska')) return 'mkv';
    if (base.includes('quicktime')) return 'mov';
    if (base.includes('3gpp')) return '3gp';
    if (base.includes('x-msvideo')) return 'avi';
    return 'mp4';
}

export function converterVideoViaWorker(
    pedido: PedidoDeConversao,
    opts: ConversaoOptions = {},
): Promise<ResultadoDaConversao> {
    const { signal, onProgress } = opts;

    return new Promise<ResultadoDaConversao>((resolve, reject) => {
        const worker = getWorker();
        const inputExt = extensaoDeEntrada(pedido.blob.type);

        const cleanup = () => {
            worker.onmessage = null;
            worker.onerror = null;
            signal?.removeEventListener('abort', onAbort);
        };
        const onAbort = () => worker.postMessage({ type: 'cancel' });

        if (signal) {
            if (signal.aborted) onAbort();
            signal.addEventListener('abort', onAbort);
        }

        worker.onmessage = (e: MessageEvent<MensagemDoWorker>) => {
            const msg = e.data;
            if (msg.type === 'progress') {
                onProgress?.(msg.value);
            } else if (msg.type === 'warm') {
                dlog(`${LOG} core de vídeo pré-carregado`);
            } else if (msg.type === 'done') {
                cleanup();
                resolve({
                    blob: new Blob([msg.buffer], { type: 'video/mp4' }),
                    remuxado: msg.remuxado,
                });
            } else if (msg.type === 'cancelled') {
                cleanup();
                reject(new DOMException(msg.message, 'AbortError'));
            } else {
                console.error(`${LOG} erro do worker:`, msg.message);
                cleanup();
                reject(new Error(msg.message));
            }
        };

        worker.onerror = (e) => {
            console.error(`${LOG} worker.onerror:`, e.message, e);
            cleanup();
            // Worker quebrado não se recupera: derruba para a próxima tentativa
            // subir um novo em vez de falar com um cadáver.
            disposeVideoWorker();
            reject(new Error(e.message || 'Worker de conversão de vídeo falhou.'));
        };

        worker.postMessage({
            type: 'convert',
            ...pedido,
            inputExt,
            base: FFMPEG_VIDEO_BASE,
            debug: DEBUG,
        });
    });
}
